import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { attachSurface } from "./loaderWorkerClient";

import { DIVER_BOUNDS, DIVER_HEAD, DIVER_PATH, LOGO_DOTS, LOGO_GOLD } from "./diverMark";
import "./Loader.scss";

/*
 * The loading screen: the Skydive mark in freefall. The gold diver holds still a little above the
 * middle of the screen while the logo's own streaks (a rounded bar with a trail of dots) flow up past
 * him in a narrow column around him, near ones fast and far ones slow, running the whole height of the
 * screen: they come in through the bottom edge and leave through the top. Option A of the logo-mark
 * concepts, made still at Chris's request 2026-10-08 ("the skydiver stays in the middle and does NOT
 * rock back and forth; the bubble flows go from the bottom of the screen to the top"), and then pulled
 * back from a full-screen field to a column ("the background field covers the whole screen; originally
 * it was just a small column around the skydiver"). The previous loader, B (bubbles assemble into the
 * diver and rise off the top), is kept to drop back in:
 * design-prototypes/loading-animation/alt/LoaderAssemble.tsx.
 *
 * The streaks do not fade in or out vertically: each is fully off screen at both ends of its run. Toward
 * the sides of the column they thin out, so the column has soft edges. Drawn on a canvas that covers the
 * screen (and follows its size) so a streak can run edge to edge vertically; not DOM nodes or CSS: it is
 * dozens of moving shapes, and this screen appears exactly when the app is busy.
 *
 * And BECAUSE it appears exactly when the app is busy, it is not drawn by the app: it is drawn by a worker on an OffscreenCanvas (public/loader-worker.v1.js, generated from this
 * file's constants and the diver's geometry by scripts/make-loader-worker.py), which has its own thread and its own animation frames, so it keeps moving while the page parses,
 * generates data or lays out the dashboard (Chris, 2026-10-17: "play regardless of what is happening in the app"). Measured before: at 4x slower CPU the main-thread version was up
 * for 1.6s at 6 frames a second. The worker is ONE, started early and kept (a worker cannot start while the main thread is busy, so one created when this loader appears would be starved
 * exactly when it matters): this component only attaches its canvas to it. The code below it - drawing on the main thread - is only the FALLBACK for a browser without
 * OffscreenCanvas or workers.
 */
const DIVER_W = 86;                       // css px, the diver's width
const DIVER_Y = 0.38;                     // the diver's height as a share of the screen: a little above the middle
const DIVER_Y_NUDGE = -7;                 // px; keeps the diver where the earlier boxed loaders put it
const DIVER_TILT = -0.07;                 // radians, fixed: the pose the mockup used; the diver does not move
const K = DIVER_W / DIVER_BOUNDS.w;       // logo units -> css px at the diver's size
const NEAR_SPEED = 230;                   // px per second
const FAR_SPEED = 95;
const NEAR_LEN = 80;                      // px, average bar length
const FAR_LEN = 46;
const COLUMN_W = 200;                     // css px: how wide the flow is, centred on the diver
const NEAR_AREA = 11000;                  // one near streak per this many px of the column (its width x the screen height)
const FAR_AREA = 7000;
const EDGE_FADE_FROM = 0.55;              // share of the column's half-width where the streaks start to thin out
const EDGE_FADE_TO = 0.2;                 // the share of opacity left at the very edge of the column
const STILL_MS = 1800;                    // the frame shown to anyone who asked for less motion

// streak colours, in the order the concept dealt them
const STREAK_COLOURS = [LOGO_DOTS[3], LOGO_GOLD, LOGO_DOTS[1], LOGO_DOTS[0], LOGO_DOTS[2], LOGO_DOTS[4], LOGO_DOTS[5]];

interface Streak {
  x: number;
  len: number;
  k: number;
  speed: number;
  cycle: number;
  offset: number;
  colour: string;
  alpha: number;
  dots: number;
  near: boolean;
}

// a small seeded generator, so the loader looks the same every time it appears
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

// how much of its opacity a streak keeps at this distance from the column's centre (0 = centre, 1 = edge)
const columnFade = (fromCentre: number) =>
  fromCentre <= EDGE_FADE_FROM
    ? 1
    : 1 - (1 - EDGE_FADE_TO) * clamp((fromCentre - EDGE_FADE_FROM) / (1 - EDGE_FADE_FROM), 0, 1);

// the streaks for a screen of this size: a column COLUMN_W wide around the diver, as tall as the screen;
// the near ones keep out of the diver's own lane
const makeStreaks = (w: number, h: number): Streak[] => {
  const random = seeded(20261008);
  const streaks: Streak[] = [];
  const half = Math.min(COLUMN_W, w) / 2;
  const gapFromDiver = Math.min(DIVER_W * 0.75, half * 0.7);
  const add = (near: boolean, count: number) => {
    for (let i = 0; i < count; i++) {
      const k = K * (near ? 1.5 : 0.85);
      const side = i % 2 ? 1 : -1;
      const x = near
        ? w / 2 + side * (gapFromDiver + random() * (half - gapFromDiver))
        : w / 2 + (random() * 2 - 1) * half;
      const len = (near ? NEAR_LEN : FAR_LEN) * (0.7 + random() * 0.6);
      const dots = near ? 4 : 3;
      // one run: from wholly below the bottom edge to wholly above the top edge, then round again
      const cycle = h + len + 13.2 * k * (dots + 1) + 60;
      streaks.push({
        x,
        len,
        k,
        speed: (near ? NEAR_SPEED : FAR_SPEED) * (0.8 + random() * 0.5),
        cycle,
        offset: random() * cycle,
        colour: STREAK_COLOURS[streaks.length % STREAK_COLOURS.length],
        alpha: (near ? 0.9 : 0.45) * columnFade(Math.abs(x - w / 2) / half),
        dots,
        near,
      });
    }
  };
  const area = half * 2 * h;
  add(false, clamp(Math.round(area / FAR_AREA), 8, 60));
  add(true, clamp(Math.round(area / NEAR_AREA), 5, 40));
  return streaks;
};

/** Draws the loader on the main thread. Only used where a worker cannot be (see above). Returns how to stop it. */
const startOnMainThread = (canvas: HTMLCanvasElement, dpr: number, reducedMotion: boolean): (() => void) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => undefined;
  const diverBody = new Path2D(DIVER_PATH);
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, DIVER_W * 0.95);
  glow.addColorStop(0, "rgba(246,186,34,1)");
  glow.addColorStop(1, "rgba(246,186,34,0)");

  let width = 0;
  let height = 0;
  let streaks: Streak[] = [];

  const disc = (x: number, y: number, r: number, colour: string, alpha: number) => {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.01, r), 0, Math.PI * 2);
    ctx.fill();
  };

  // a streak as the logo draws it: a rounded bar with a trail of dots under it
  const drawStreak = (s: Streak, now: number) => {
    const barWidth = 12.5 * s.k;
    const radius = 6.2 * s.k;
    const gap = 13.2 * s.k;
    const top = height + 30 - (((now / 1000) * s.speed + s.offset) % s.cycle);
    if (top > height || top + s.len + gap * (s.dots + 1) < 0) return;       // wholly off screen
    ctx.globalAlpha = s.alpha;
    ctx.fillStyle = s.colour;
    ctx.beginPath();
    ctx.moveTo(s.x - barWidth / 2, top + radius);
    ctx.arc(s.x, top + radius, radius, Math.PI, 0);
    ctx.lineTo(s.x + barWidth / 2, top + s.len - radius);
    ctx.arc(s.x, top + s.len - radius, radius, 0, Math.PI);
    ctx.closePath();
    ctx.fill();
    for (let i = 0; i < s.dots; i++) {
      disc(s.x, top + s.len + gap * (i + 0.9), radius * (1 - i * 0.13), s.colour, s.alpha * (1 - i / (s.dots + 0.6)));
    }
  };

  const drawDiver = (x: number, y: number) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(DIVER_TILT);
    ctx.scale(K, K);
    ctx.translate(-(DIVER_BOUNDS.x + DIVER_BOUNDS.w / 2), -(DIVER_BOUNDS.y + DIVER_BOUNDS.h / 2));
    ctx.globalAlpha = 1;
    ctx.fillStyle = LOGO_GOLD;
    ctx.fill(diverBody);
    ctx.beginPath();
    ctx.ellipse(DIVER_HEAD.cx, DIVER_HEAD.cy, DIVER_HEAD.rx, DIVER_HEAD.ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const draw = (now: number) => {
    ctx.clearRect(0, 0, width, height);
    const diverX = width / 2;
    const diverY = height * DIVER_Y + DIVER_Y_NUDGE;
    ctx.save();
    ctx.translate(diverX, diverY);
    ctx.globalAlpha = 0.17;
    ctx.fillStyle = glow;
    ctx.fillRect(-DIVER_W, -DIVER_W, DIVER_W * 2, DIVER_W * 2);
    ctx.restore();
    streaks.forEach((s) => !s.near && drawStreak(s, now));
    drawDiver(diverX, diverY);
    streaks.forEach((s) => s.near && drawStreak(s, now));
    ctx.globalAlpha = 1;
  };

  // the canvas covers the screen: size it to the screen, and again whenever the window changes
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = Math.max(1, Math.round(rect.width));
    height = Math.max(1, Math.round(rect.height));
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    streaks = makeStreaks(width, height);
    if (reducedMotion) draw(STILL_MS);       // for anyone who asked for less motion: one still frame
  };
  resize();
  const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
  if (observer) observer.observe(canvas);
  else window.addEventListener("resize", resize);

  let raf = 0;
  if (!reducedMotion) {
    const frame = (now: number) => {
      draw(now);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
  }
  return () => {
    cancelAnimationFrame(raf);
    if (observer) observer.disconnect();
    else window.removeEventListener("resize", resize);
  };
};

/** Attaches a canvas to the page's loader worker (see loaderWorkerClient.ts). Returns how to detach it, or null when this browser cannot (no workers, or no OffscreenCanvas). */
const startInWorker = (canvas: HTMLCanvasElement, dpr: number, reducedMotion: boolean, onFailed: () => void): (() => void) | null => attachSurface(canvas, dpr, reducedMotion, { onFailed });

const Loader: FC = () => {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reducedMotion = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // A canvas can be handed to a worker once, so each start gets a canvas of its own (this effect also runs twice in development's strict mode).
    let canvas: HTMLCanvasElement | null = null;
    let stop: () => void = () => undefined;
    const fresh = () => {
      canvas?.remove();
      canvas = document.createElement("canvas");
      canvas.className = "loading-mark";
      host.appendChild(canvas);

      return canvas;
    };
    const onMainThread = () => {
      stop();
      stop = startOnMainThread(fresh(), dpr, reducedMotion);
    };
    const inWorker = startInWorker(fresh(), dpr, reducedMotion, onMainThread);
    if (inWorker) {
      stop = inWorker;
    } else {
      onMainThread();
    }
    return () => {
      stop();
      canvas?.remove();
    };
  }, []);

  // Drawn straight into <body>, never where the caller happens to sit. A `position: fixed` element is fixed to the SCREEN only if no ancestor creates a containing block, and a
  // `backdrop-filter`, `filter` or `transform` on any ancestor does exactly that: the admin pages' card (`.admin-theme-card`, backdrop-filter: blur) trapped the loader inside itself,
  // so it started below the top bar, was clipped at the card's edge, and left the bar and sidebar undimmed (Chris, 2026-10-20: "Top bar of the admin section is overlapping the loading
  // animation"). In <body> no layout can do that, now or when someone adds a blur to another container.
  return createPortal(
    <div className="loading-main" role="status" aria-live="polite" aria-label="Loading">
      <div ref={hostRef} className="loading-mark-host" />
    </div>,
    document.body
  );
};
export default Loader;
